import { MainHeader, Section, SectionParagraph } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import sleepingCat from '@/static/assets/pet/cat/cat_sleeping_hero.svg';
import sleepingCatMobile from '@/static/assets/pet/cat/cat_sleeping_hero_mobile.svg';
import sleepingDog from '@/static/assets/pet/dog/dog_sleeping_hero.svg';
import sleepingDogMobile from '@/static/assets/pet/dog/dog_sleeping_hero_mobile.svg';
import { PRODUCT_PET } from '@/utils/products';
import { Flex, ResponsiveBreak, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import type React from 'react';
import styled from 'styled-components';
import { CheckMarkIcon, PetLandingPageType } from './shared';

const imagesMap = {
  [PetLandingPageType.Dog]: {
    desktopSrc: sleepingDog,
    mobileSrc: sleepingDogMobile,
  },
  [PetLandingPageType.Cat]: {
    desktopSrc: sleepingCat,
    mobileSrc: sleepingCatMobile,
  },
  [PetLandingPageType.PetInsurance]: {
    desktopSrc: sleepingDog,
    mobileSrc: sleepingDogMobile,
  },
};

const HeroWrapper = styled(Section)`
  width: 100%;
  padding-top: 40px;
`;

const Heading = styled(MainHeader)`
  margin-bottom: 16px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 24px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 20px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 18px;
    `}
`;

export const MainCTA = styled(JoinCTA)`
  font-size: 1.6rem;
  width: 197px;
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 32px;
        width: 288px;
    `}
`;

const AnimationWrapper = styled(Flex)<{
  petLandingPageType: PetLandingPageType;
}>`
  background: linear-gradient(
    ${themedColor('white')},
    ${themedColor('white')}
      ${({ petLandingPageType }) => (petLandingPageType === PetLandingPageType.Cat ? '53%' : '56%')},
    ${themedColor('cloud')}
      ${({ petLandingPageType }) => (petLandingPageType === PetLandingPageType.Cat ? '53%' : '56%')},
    ${themedColor('cloud')}
  );
  width: 100%;
  justify-content: center;

  & > img {
    max-width: 100%;
    object-fit: cover;
    width: 298px;
    height: ${({ petLandingPageType }: { petLandingPageType: PetLandingPageType }) =>
      petLandingPageType === PetLandingPageType.Cat ? 197 : 176}px;
  }

  & > img[data-display='desktop'] {
    display: none;
  }

  & > img[data-display='mobile'] {
    display: block;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        & > img {
            width: ${({ petLandingPageType }: { petLandingPageType: PetLandingPageType }) =>
              petLandingPageType === PetLandingPageType.Cat ? 627 : 617}px;
            height: ${({ petLandingPageType }: { petLandingPageType: PetLandingPageType }) =>
              petLandingPageType === PetLandingPageType.Cat ? 409 : 365}px;
        }

        & > img[data-display='desktop'] {
            display: block;
        }

        & > img[data-display='mobile'] {
            display: none;
        }
    `}
`;

const AdvantagesList = styled(Flex).attrs({
  as: 'ul',
  flexWrap: 'wrap',
  justifyContent: 'center',
})`
  padding: 0;
  list-style-type: none;
`;

const Advantages = styled(AdvantagesList)`
  margin-bottom: 24px;
  display: flex;

  ${MEDIA_QUERIES.mobile`
        column-gap: 12px;
        row-gap: 8px;
    `}
`;

const AdvantageListItem = styled.li`
  display: inline-flex;
  ${font('main', { fontSize: '1.4rem' })};
  gap: 5px;
  align-items: center;

  ${MEDIA_QUERIES.tabletAndDesktop`
        font-size: 1.6rem;

        &:not(:first-of-type) {
            margin-left: 24px;
        }
    `}
`;

const advantages = {
  [PetLandingPageType.Dog]: [
    'Rest easy',
    'Prepare for the unexpected',
    'Take the bite out of vet bills',
    'Save on routine care',
  ],
  [PetLandingPageType.Cat]: [
    'Get peace of mind',
    'Keep your cat healthy',
    'Don’t stress about vet costs',
    'Save on routine care',
  ],
  [PetLandingPageType.PetInsurance]: [
    'Get peace of mind',
    'Keep your pet healthy',
    'Don’t stress about vet costs',
    'Save on routine care',
  ],
};

const mainTitle = {
  [PetLandingPageType.Dog]: 'Dog insurance',
  [PetLandingPageType.Cat]: 'Cat insurance',
  [PetLandingPageType.PetInsurance]: (
    <>
      <span>Tailored for Your</span>
      <ResponsiveBreak display={['block', 'inline']} />
      <span>Best Friend</span>
    </>
  ),
};

const title = {
  [PetLandingPageType.Dog]: 'at a fetching price',
  [PetLandingPageType.Cat]: 'tailored for your feline',
  [PetLandingPageType.PetInsurance]: 'Pet Insurance',
};

const subTitle = {
  [PetLandingPageType.Dog]: 'Coverage that’s perfect for your pup',
  [PetLandingPageType.Cat]: 'Protection perfectly curated for your cat',
  [PetLandingPageType.PetInsurance]: 'Protection just right for your pet',
};

interface InternalHeroProps {
  petLandingPageType: PetLandingPageType;
}

export const InternalHero: React.FC<InternalHeroProps> = ({ petLandingPageType }) => {
  const { ref } = useShowHeaderCtaOnScroll();

  return (
    <HeroWrapper justifyContent="center">
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>
          {mainTitle[petLandingPageType]}
          <ResponsiveBreak display={['block', 'block']} />
          {title[petLandingPageType]}
        </Heading>
        <SubHeading>
          {subTitle[petLandingPageType]}
          <ResponsiveBreak display={['block', 'inline']} />
          (and your pocket)
        </SubHeading>
        <Advantages>
          {advantages[petLandingPageType].map(advantage => (
            <AdvantageListItem key={advantage}>
              <CheckMarkIcon />
              {advantage}
            </AdvantageListItem>
          ))}
        </Advantages>
        <MainCTA
          ref={ref}
          size="big"
          flavor="primary"
          glowing
          data-source="pet"
          product={PRODUCT_PET}
          analyticsParams={{ position: 'top', pet_lp_type: petLandingPageType, variant: petLandingPageType }}
        >
          Check our prices
        </MainCTA>
      </Flex>
      <AnimationWrapper petLandingPageType={petLandingPageType}>
        <Image
          data-display="desktop"
          alt="peace of mind for pet insurance"
          src={imagesMap[petLandingPageType].desktopSrc}
        />
        <Image
          data-display="mobile"
          alt="peace of mind for pet insurance"
          src={imagesMap[petLandingPageType].mobileSrc}
        />
      </AnimationWrapper>
    </HeroWrapper>
  );
};
